import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import loadInventoryForDataLayer from "@MasterBluePrint/Handlers/LoadInventoryForDataLayer";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        currentPageUrl,
        state,
    };
};

const mapDispatchToProps = {
    loadInventoryForDataLayer,
};

interface OwnProps {
    carouselType: string;
    product: CartLineModel | ProductContextModel | OrderLineModel;
    extendedStyles?: ProductDescriptionStyles;
    index?: number;
    carouselTitle: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;

            @media print {
                display: block !important;
            }
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
};

const ProductDescription: FC<Props> = ({
    carouselType,
    product,
    extendedStyles,
    category,
    currentPageUrl,
    index,
    carouselTitle,
    loadInventoryForDataLayer,
}) => {
    const [styles] = React.useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const productId = "product" in product ? product.product.id : "productId" in product ? product.productId : "";
    const unitOfMeasure =
        "product" in product
            ? product.productInfo?.unitOfMeasure
            : "unitOfMeasure" in product
            ? product.unitOfMeasure
            : "";

    const productClickDataLayerEvent = () => {
        if (productId) {
            loadInventoryForDataLayer({
                productId,
                listType: carouselType,
                unitOfMeasure,
                product,
                currentPageUrl,
                carouselTitle,
                category,
                itemLinkClick: 'Product Description',
            });
        }
    };

    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            data-test-selector="productDescriptionLink"
            onClick={productClickDataLayerEvent}
            className="product-detail-link"
        >
            {description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductDescription);
